import { useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronRight,
  ChevronDown,
  Wrench,
  AlertCircle,
  Headphones,
  MessageSquare,
  Wifi,
  Calculator,
  FileText,
  Users,
  Calendar,
  CalendarDays,
  IndianRupee,
  Lock,
  BarChart3,
  Send,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const COMMON_ISSUES = [
  { key: "payroll", label: "Payroll Calculation", desc: "Issues with payroll processing or calculations", Icon: Calculator, tint: "sc-ic-blue" },
  { key: "payslip", label: "Payslip Issues", desc: "Problems viewing or downloading payslips", Icon: FileText, tint: "sc-ic-violet" },
  { key: "attendance", label: "Attendance Issues", desc: "Attendance marking or import problems", Icon: Calendar, tint: "sc-ic-green" },
  { key: "leave", label: "Leave Balance", desc: "Leave balance discrepancies or allocation", Icon: CalendarDays, tint: "sc-ic-teal" },
  { key: "employee", label: "Employee Data", desc: "Employee record or profile issues", Icon: Users, tint: "sc-ic-amber" },
  { key: "salary", label: "Salary Component", desc: "Salary structure or component configuration", Icon: IndianRupee, tint: "sc-ic-rose" },
  { key: "login", label: "Login / Access", desc: "Login problems or access permission issues", Icon: Lock, tint: "sc-ic-slate" },
  { key: "reports", label: "Reports Issues", desc: "Report generation or data export problems", Icon: BarChart3, tint: "sc-ic-indigo" },
];

const TROUBLESHOOTING = [
  {
    q: "Payroll not processing correctly",
    a: "1. Verify all attendance data is finalized for the period.\n2. Check that salary structures are assigned to all employees.\n3. Ensure statutory settings (PF, ESI, PT) are properly configured.\n4. Re-process payroll and review the summary for discrepancies.",
  },
  {
    q: "Employee not appearing in payroll",
    a: "1. Confirm the employee's status is set to 'Active'.\n2. Check that the joining date falls within the payroll period.\n3. Verify a salary structure is assigned to the employee.\n4. Ensure the employee is added to the correct pay group.",
  },
  {
    q: "Attendance data not syncing",
    a: "1. Check if biometric device is online and connected.\n2. Verify device-employee mapping is configured correctly.\n3. Try manually importing attendance data.\n4. Check attendance configuration for correct shift assignments.",
  },
  {
    q: "Unable to generate payslips",
    a: "1. Ensure payroll is finalized for the selected period.\n2. Check if the payslip template is configured.\n3. Verify employee salary data is complete.\n4. Try regenerating payslips from the Payroll > Payslips section.",
  },
  {
    q: "Leave balance showing incorrect",
    a: "1. Review the leave policy assigned to the employee.\n2. Check leave allocation and carry-forward settings.\n3. Verify that leave adjustments are correctly applied.\n4. Recalculate leave balances from Leave > Admin.",
  },
];

const ERROR_MESSAGES = [
  {
    q: "Salary structure not found",
    a: "This error occurs when an employee doesn't have a salary structure assigned. Go to Salary > Structures and assign the appropriate structure to the employee.",
  },
  {
    q: "Payroll period already finalized",
    a: "You cannot reprocess a finalized payroll period. To make changes, revert the payroll first from Payroll > Process Hub, then make corrections and re-finalize.",
  },
  {
    q: "Attendance data missing for employees",
    a: "Some employees don't have attendance records for the payroll period. Import attendance data or mark attendance manually before processing payroll.",
  },
  {
    q: "Insufficient leave balance",
    a: "The employee doesn't have enough leave balance for the requested leave type. Check the leave allocation in Leave > Admin or adjust the balance manually.",
  },
  {
    q: "PF/ESI configuration incomplete",
    a: "Statutory settings are not fully configured. Go to Settings > Statutory and ensure PF number, ESI number, and contribution rates are correctly set up.",
  },
];

export default function SupportCenter() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openTroubleshoot, setOpenTroubleshoot] = useState(null);
  const [openError, setOpenError] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredIssues = useMemo(() => {
    if (!q) return COMMON_ISSUES;
    return COMMON_ISSUES.filter(
      (i) => i.label.toLowerCase().includes(q) || i.desc.toLowerCase().includes(q)
    );
  }, [q]);

  const filteredTroubleshooting = useMemo(() => {
    if (!q) return TROUBLESHOOTING;
    return TROUBLESHOOTING.filter(
      (t) => t.q.toLowerCase().includes(q) || t.a.toLowerCase().includes(q)
    );
  }, [q]);

  const filteredErrors = useMemo(() => {
    if (!q) return ERROR_MESSAGES;
    return ERROR_MESSAGES.filter(
      (e) => e.q.toLowerCase().includes(q) || e.a.toLowerCase().includes(q)
    );
  }, [q]);

  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
    },
    [q]
  );

  return (
    <div className="brix-page">
      {/* ── Hero ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-blue">
              <Wrench size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Support Center</h1>
              <p className="hs-hero-sub">
                Need help with BrixiGo? We're here to help you resolve issues quickly.
                Search for a solution or contact our support team.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={handleSearch}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search your issue — e.g. payroll error, login problem, leave balance..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* ── Common Issues ── */}
      <div ref={resultsRef} style={{ marginBottom: 24 }}>
        <div className="hs-section-head">
          <h2 className="hs-section-title">Common Issues</h2>
          <p className="hs-section-sub">Select your issue category to find solutions</p>
        </div>

        {q && filteredIssues.length === 0 && (
          <div className="sc-no-results">
            No issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
          </div>
        )}

        <div className="sc-issues-grid">
          {filteredIssues.map((issue) => {
            const Icon = issue.Icon;
            return (
              <div
                key={issue.key}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                onClick={
                  issue.key === "payroll" ? () => navigate("/support/payroll") :
                  issue.key === "payslip" ? () => navigate("/support/payslip") :
                  issue.key === "attendance" ? () => navigate("/support/attendance") :
                  issue.key === "leave" ? () => navigate("/support/leave") :
                  issue.key === "employee" ? () => navigate("/support/employee") :
                  issue.key === "salary" ? () => navigate("/support/salary") :
                  issue.key === "login" ? () => navigate("/support/login") :
                  issue.key === "reports" ? () => navigate("/support/reports") :
                  undefined
                }
                onKeyDown={
                  issue.key === "payroll" ? (e) => e.key === "Enter" && navigate("/support/payroll") :
                  issue.key === "payslip" ? (e) => e.key === "Enter" && navigate("/support/payslip") :
                  issue.key === "attendance" ? (e) => e.key === "Enter" && navigate("/support/attendance") :
                  issue.key === "leave" ? (e) => e.key === "Enter" && navigate("/support/leave") :
                  issue.key === "employee" ? (e) => e.key === "Enter" && navigate("/support/employee") :
                  issue.key === "salary" ? (e) => e.key === "Enter" && navigate("/support/salary") :
                  issue.key === "login" ? (e) => e.key === "Enter" && navigate("/support/login") :
                  issue.key === "reports" ? (e) => e.key === "Enter" && navigate("/support/reports") :
                  undefined
                }
                style={{ cursor: "pointer" }}
              >
                <div className={`sc-issue-icon ${issue.tint}`}>
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <div className="sc-issue-name">{issue.label}</div>
                <div className="sc-issue-desc">{issue.desc}</div>
                <div className="sc-issue-link">
                  View solutions <ChevronRight size={14} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Troubleshooting & Error Messages ── */}
      <div className="sc-two-col">
        {/* Troubleshooting */}
        <div className="sc-col-card">
          <div className="sc-col-header">
            <div className="sc-col-header-icon sc-icon-blue">
              <Wrench size={18} strokeWidth={2} />
            </div>
            <h2 className="hs-section-title" style={{ marginBottom: 0 }}>Troubleshooting</h2>
          </div>
          <div className="sc-accordion-list">
            {q && filteredTroubleshooting.length === 0 && (
              <div className="sc-no-results-sm">No troubleshooting guides match your search.</div>
            )}
            {filteredTroubleshooting.map((item, idx) => {
              const isOpen = openTroubleshoot === idx;
              return (
                <div key={item.q} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenTroubleshoot(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.q}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          {item.a.split("\n").map((line, i) => (
                            <p key={i} style={{ margin: "4px 0" }}>{line}</p>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Messages */}
        <div className="sc-col-card">
          <div className="sc-col-header">
            <div className="sc-col-header-icon sc-icon-rose">
              <AlertCircle size={18} strokeWidth={2} />
            </div>
            <h2 className="hs-section-title" style={{ marginBottom: 0 }}>Error Messages</h2>
          </div>
          <div className="sc-accordion-list">
            {q && filteredErrors.length === 0 && (
              <div className="sc-no-results-sm">No error messages match your search.</div>
            )}
            {filteredErrors.map((item, idx) => {
              const isOpen = openError === idx;
              return (
                <div key={item.q} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenError(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.q}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">{item.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Contact Support ── */}
      <div className="sc-contact-card">
        <div className="sc-contact-inner sc-contact-row">
          <div className="sc-contact-icon">
            <Headphones size={24} strokeWidth={1.5} />
          </div>
          <div className="sc-contact-text">
            <h2 className="sc-contact-title">Still need help?</h2>
            <p className="sc-contact-sub">
              Our support team is here to assist you. Raise a support request and we'll get back to you.
            </p>
          </div>
          <button type="button" className="sc-contact-btn">
            <Send size={16} />
            Raise Support Request
          </button>
        </div>
      </div>

      {/* ── My Support Requests ── */}
      <div className="sc-requests-card">
        <div className="sc-requests-header">
          <h2 className="hs-section-title" style={{ marginBottom: 0 }}>My Support Requests</h2>
          <div className="sc-requests-badge">Coming Soon</div>
        </div>
        <div className="sc-requests-empty">
          <MessageSquare size={32} style={{ color: "#cbd5e1" }} />
          <div className="sc-requests-empty-title">No support requests yet</div>
          <div className="sc-requests-empty-sub">
            When you raise a support request, it will appear here so you can track its progress.
          </div>
        </div>
      </div>

      {/* ── System Status ── */}
      <div className="sc-status-bar">
        <div className="sc-status-left">
          <Wifi size={16} />
          <div className="sc-status-dot" />
          <span className="sc-status-text">All systems operational</span>
        </div>
        <span className="sc-status-time">Last checked: Just now</span>
      </div>
    </div>
  );
}
